import {
  type AcceptInviteRequest,
  type AcceptInviteResponse,
  type InvitePreviewResponse,
  InvitePreviewResponseSchema,
  type SwitchOrganizationResponse,
} from '@shared/dto/users';
import type { OrganizationDTO } from '@shared/dto/organizations';
import type { AppAbility } from '@shared/dto/rbac';

import { callApi, callApiJson } from '../api';

/**
 * The statuses the invite routes use to say that an invite cannot be used. The invite page
 * handles them as answers, so they are not reported as errors.
 */
const INVITE_REFUSAL_STATUSES = [403, 404];

/**
 * Verify user login with email
 * @param email User's email address
 * @param fromOidc Whether this login is from an OIDC flow (optional, defaults to false)
 * @returns Promise with the token data
 */
export async function verifyUserLogin(email: string, fromOidc: boolean = false) {
  return callApiJson('/users/login', {
    method: 'POST',
    body: JSON.stringify({
      email,
      fromOidc,
    }),
  });
}

/**
 * Request magic link login
 * @param email User's email address
 * @returns Promise with the response data
 */
export async function dangerousPublicLogin(email: string) {
  await callApi('/users/login', {
    method: 'POST',
    body: JSON.stringify({
      email,
    }),
  });
}

/**
 * Get current user information
 * @returns Promise with the user data
 */
export async function getCurrentUser() {
  return callApiJson('/users/me');
}

export const getUserTeamAbilities = async (teamId: string): Promise<AppAbility> => {
  return await callApiJson(`/users/me/abilities?teamId=${teamId}`);
};

export async function getUserOrganizations(options?: {
  handle401Locally?: boolean;
}): Promise<OrganizationDTO[]> {
  return await callApiJson(
    '/users/me/organizations',
    {},
    { skip401Redirect: options?.handle401Locally },
  );
}

export async function switchOrganization(
  organizationId: string,
): Promise<SwitchOrganizationResponse> {
  return await callApiJson(
    '/users/me/switch-organization',
    {
      method: 'POST',
      body: JSON.stringify({ organizationId }),
    },
    { skip401Redirect: true },
  );
}

/**
 * Asks which organization the invite code of a share link admits to, and whether the signed-in
 * user already belongs to it. Nothing changes on the server.
 */
export async function previewInvite(inviteCode: string): Promise<InvitePreviewResponse> {
  const json = await callApiJson(
    '/users/me/invites/preview',
    {
      method: 'POST',
      body: JSON.stringify({ invite_code: inviteCode }),
    },
    // The invite page handles expired sessions locally. A delayed 401 must never log out
    // a replacement session established by another tab while this request was in flight.
    { skip401Redirect: true, suppressErrorLogStatuses: INVITE_REFUSAL_STATUSES },
  );
  return InvitePreviewResponseSchema.parse(json);
}

/**
 * Accepts an invite for the signed-in user: the server adds them to the organization, if they are
 * not a member yet, and moves this session into it. The expected user is the account displayed
 * when the person confirms; a replacement browser session must review the invite again.
 */
export async function acceptInvite(request: AcceptInviteRequest): Promise<AcceptInviteResponse> {
  return await callApiJson(
    '/users/me/invites/accept',
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
    // The invite page handles expired sessions locally. A delayed 401 must never log out
    // a replacement session established by another tab while this request was in flight.
    { skip401Redirect: true, suppressErrorLogStatuses: [...INVITE_REFUSAL_STATUSES, 409] },
  );
}
